import { useSearchParams } from "react-router";
import { useEffect, useState } from "react";
import { FormProvider, useFormContext, UseFormReturn } from "react-hook-form";
import { FormContent, FormControl, FormField, FormItem, FormMessage } from "@core/components/ui/Form";
import { Input } from "@core/components/ui";
import { cn } from "@core/utils";
import z from "zod";
import { FormInput } from "@core/components/form/FormInput";

export const ipInputFormSchema = z.object({
  ip: z
    .string({ message: "ip는 문자여야 합니다" })
    .regex(/^\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}$/, { message: "ip는 올바른 ip 형식이어야 합니다" })
    .or(z.literal("")),
});
export type IpInputFormSchema = z.infer<typeof ipInputFormSchema>;

export const FormInputIp = <T extends { ip?: string }>({ form, initValues, useParams }: { form: UseFormReturn<T>; initValues: { ip: string }; useParams: boolean }) => {
  const { values } = useFormInputIpInitValuesBySearchParams({ defaultValues: initValues, useParams });

  return (
    <FormProvider {...form}>
      <FormInputIpForm initValues={values} />
    </FormProvider>
  );
};

export const FormInputIpForm = ({ initValues }: { initValues: IpInputFormSchema }) => {
  const form = useFormContext<IpInputFormSchema>();

  useEffect(() => {
    form.setValue("ip", initValues.ip || "");
    form.trigger("ip");
  }, [initValues.ip]);

  return (
    <div className={cn("mb-[1px] flex min-h-[56px] w-full flex-row items-stretch justify-between", "lg:gap-[2px]")}>
      {/* <div className={cn("col-gray-700 flex h-full w-[44px] min-w-[44px] items-center justify-center bg-gray-200 p-2 text-sm font-bold", "lg:w-[120px] lg:text-lg")}>IP</div> */}
      <div className="col-gray-700 flex h-auto w-[120px] min-w-[44px] items-center justify-center bg-gray-200 p-2 text-sm font-bold lg:text-lg">IP</div>

      <div className="flex h-auto w-full min-w-[285px] flex-1 flex-row flex-wrap items-center gap-[8px] bg-gray-50 py-[10px] pl-[12px] lg:min-w-[740px] lg:gap-[28px] lg:px-[30px] lg:py-0">
        <div className="flex h-full w-full items-center">
          <FormField
            control={form.control}
            name="ip"
            render={({ field }) => (
              <FormItem className={cn("wrapper-form-item form-input")}>
                <FormContent className={cn("relative flex flex-col items-start lg:py-[5px]")}>
                  <FormControl>
                    <Input
                      {...field}
                      // onChange={(e) => {
                      // field.onChange(e); // react-hook-form 상태 갱신, 다른 동작없으면 ...field로 처리됨
                      // }}
                      className="w-[200px]"
                      onInput={(e) => {
                        e.currentTarget.value = e.currentTarget.value.replace(/[^0-9.]/g, ""); // 유효성 검사
                      }}
                    />
                  </FormControl>
                  {/* 메시지 위치 때문에 FormInput 사용이 어려움 */}
                  <FormMessage className={cn("mt-[5px]")} />
                </FormContent>
              </FormItem>
            )}
          />

          {/* <FormInput name="ip" control={form.control} label="IP" required itemClassName="mt-3" /> */}
        </div>
      </div>
    </div>
  );
};

/**
 * 검색 파라미터를 통해 FormInputIp 초기값 설정
 * @returns 초기값
 */
export const useFormInputIpInitValuesBySearchParams = ({ defaultValues, useParams }: { defaultValues: { ip: string }; useParams: boolean }) => {
  const [values, setValues] = useState<{ ip: string }>(defaultValues || { ip: "" });
  const [searchParams] = useSearchParams();

  useEffect(() => {
    if (useParams) {
      const ip = searchParams.get("ip");

      // 벨리데이션 통과 했을때만
      const result = ipInputFormSchema.safeParse({ ip });

      if (ip && result.success) {
        setValues({ ip });
      } else {
        setValues({ ip: "" });
      }
    }
  }, [searchParams]);

  return { values, setValues };
};
